import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTimeZone } from "./AppTimeZone";
import { AppSendHistory } from "./AppSendHistory";
import { AppDemo } from "./AppDemo";

// AppSignUpApi - what the page sends to `/claim`, `/pair` and `/dropbox`, and how it reads back.
//
// ⚠ ONE RULES MODULE FOR THE THREE, DELIBERATELY. They are one conversation - claim, link, connect
// storage - carried over one wire with one answer shape, and three near-identical copies of "read
// the body whatever the status" would be three places for the status-0 rule to drift.
//
// ⚠ IT IS SEPARATE FROM THE TRANSPORT FOR THE REASON `AppEnrol` IS SEPARATE FROM `AppEnrolPost`,
// and it was measured the same way: the Bridge suite cross-checks both halves of the wire and
// therefore imports this file, so a version that also held `XMLHttpRequest` would drag a browser
// global into a Node typecheck - passing under tsx and failing under tsc. The rules have no
// habitat; the request does.
//
// ==================================================================================================
// !! ⛔ THE SESSION SECRET LIVES IN MEMORY AND NOWHERE ELSE
// ==================================================================================================
//
// `/claim` hands it back once. It authorises requesting a pairing code against that number, and -
// once the handset has proved the number - pointing that subscriber's entire archive at a Dropbox.
//
// ⛔ SO IT IS NEVER PUT IN `localStorage`, `sessionStorage`, A COOKIE OR THE URL. Storage outlives
// the tab and is readable by anything that gets script into this origin; a URL is in history, in a
// referrer and in anything the visitor pastes. ⚠ THE COST IS THAT A RELOAD LOSES THE SIGN-UP and
// the visitor claims again - which is correct, and cheap, because `ClaimGate` lets the same person
// re-claim their own provisional number.

/** Everything any of the three endpoints can answer. Unset fields stay empty. */
export class SignUpAnswer {
    public Ok: boolean = false;
    /** A sentence to show. ⚠ VALIDATION, never routed to the error handler (law 1). */
    public Why: string = "";
    /** `/claim`: the normalised number the service filed. */
    public Number: string = "";
    /** `/claim`: the session secret. ⛔ Held in memory only - see the header. */
    public Secret: string = "";
    /** `/pair`: the code to type into the handset, or "". */
    public Code: string = "";
    /**
     * `/pair`: the phone is linked - the end of that step. `/folder`: this subscriber's WhatsApp is
     * usable right now. ⛔ On `/folder` read it ONLY together with `LinkKnown` - see there.
     */
    public Linked: boolean = false;
    /**
     * `/folder`: the service said whether WhatsApp is linked at all.
     *
     * ⛔ THE RE-LINK BANNER NEEDS `LinkKnown && !Linked`, NEVER `!Linked` ALONE. The service leaves
     * `linked` out when it cannot tell (a subscriber on another container, one still starting, an
     * older service build), and reading that absence as `false` would tell somebody whose phone is
     * fine to go and re-link it.
     */
    public LinkKnown: boolean = false;
    /** `/pair`: no answer yet. ⚠ The normal case for the first cycle, not a failure. */
    public Waiting: boolean = false;
    /**
     * `/pair`: this refusal is WhatsApp demanding a passkey for the account (`passkey: true`, the Bridge's
     * `PairReply.PasskeyField`). ⭐ A FLAG, NEVER A SENTENCE MATCH (the `NoStorage` rule): the page shows the
     * sentence as a notice about the account, in ink - it is not a refusal of something the visitor typed.
     * ⚠ Absent (an older service) is false, and the sentence keeps the ordinary refusal look.
     */
    public Passkey: boolean = false;
    /**
     * `/claim`: this refusal is "WACommander is full" (`full: true`, the Bridge's `ClaimRules.FullField`) - a
     * condition at our end, not something wrong with the number typed. ⭐ A FLAG, NEVER A SENTENCE MATCH, exactly
     * like `Passkey`: the page shows the sentence as a notice in ink. Absent (an older service) is false.
     */
    public Full: boolean = false;
    /**
     * `/dropbox` POST: this service KEEPS the messages WhatsApp hands over when a device is linked (`history: true`,
     * the Bridge's `DropboxGrantRules.HistoryField`, sent only while its history sync is switched on). ⭐ A FLAG, like
     * `Passkey`: the history step's "no backup" words are chosen by it. Absent (off, or an older service) is false.
     */
    public History: boolean = false;
    /** `/dropbox` GET: where to send the visitor to approve. */
    public Url: string = "";
    /** `/dropbox` POST: whose Dropbox it landed in, for showing back. */
    public Account: string = "";
    /** `/storage`: a short-lived, write-only Dropbox token. ⛔ Held in memory only, like the
     *  secret - it is a bearer credential for the subscriber's own storage. */
    public Token: string = "";
    /** `/storage`: how many seconds that token has left. */
    public Expires: number = 0;
    /** `/storage`: where the service wants the file put. ⚠ The service names it, not the page. */
    public Path: string = "";
    /**
     * `/folder`: a durable archive-read key, or "".
     *
     * ⛔ "" MEANS "KEEP THE ONE YOU HAVE" AND MUST NEVER BE STORED OVER A GOOD KEY. The service
     * returns a key only when it has just minted AND stored one; every ordinary visit answers with
     * none, because nothing changed. Writing "" on those visits would turn a thirty-day credential
     * into a single-use one, silently, and only for subscribers who came back.
     */
    public Key: string = "";
    /** `/send`: the message id the service accepted or reports on. */
    public Id: string = "";
    /** `/send`: accepted into the outbox. ⚠ Not sent - see `Sent`. */
    public Queued: boolean = false;
    /** `/send` check: WhatsApp accepted it. ⛔ Read as strictly `true` - see `Fields`. */
    public Sent: boolean = false;
    /**
     * `/send` check: WhatsApp did not confirm in time, so it MAY have been delivered. FINAL - the
     * sender never tries again - and `Why` carries its sentence. ⛔ Neither "sent" nor "not sent":
     * see `SendRules.StatusFrom`. Must match the field `OutboxRun` writes (`SendContract.test.ts`).
     */
    public Unconfirmed: boolean = false;
    /** `/send` check: ⭐ TRANSLATION - what went out, when it differs from what the owner typed; else "". */
    public SentText: string = "";
    /** `/send` check: ⛔ the words could not be translated - NOTHING was sent (Retry / Send in English). */
    public TranslateFailed: boolean = false;
    /**
     * `/folder`: the refusal means "no Dropbox connected to this number yet".
     *
     * ⭐ IT IS WHAT TURNS A DEAD END INTO THE DROPBOX STEP (2026-09-24). A sign-up that lost its
     * secret to a reload after WhatsApp linked lands here with only an archive key, and the page
     * offers "Connect your Dropbox" with that key as the proof. ⛔ A flag, never a sentence match -
     * the wording will be improved one day and the match would silently stop.
     */
    public NoStorage: boolean = false;
    /**
     * `/folder`: the key or session was DEFINITELY refused (`FolderTicketRules.Refused(.., Stale)`).
     * ⛔ The ONLY answer on which the page forgets its stored key - a lost connection, a 5xx or an older
     * service without the flag keeps it (never forget on uncertainty).
     */
    public Stale: boolean = false;
    /**
     * `/folder`: this browser is the OWNER'S own (`owner: true`), so the footer may offer the Claude
     * analysis panel. ⚠ Held in memory only; absent is false.
     */
    public Owner: boolean = false;
    /**
     * ⭐ WAVE 3 (DORMANT): `/folder` / `/dropbox`-like answers say where the archive lives - "onedrive" or "dropbox" -
     * only while the service's OneDrive switch is on. "" = not said (off, or an older service) = Dropbox.
     */
    public Storage: string = "";
    /** `/storage` for a OneDrive archive: the pre-authenticated upload address (no token). "" otherwise. */
    public UploadUrl: string = "";
}

/**
 * How one `/send` check came out, for the page.
 *
 * ⚠ FOUR OUTCOMES, NOT THREE. `Sent`; `Unconfirmed` (final, fate unknown - check the phone);
 * `Final` without either (refused, with the service's sentence); or none (keep polling).
 */
export class AppSendVerdict {
    public Sent: boolean = false;
    public Final: boolean = false;
    public Unconfirmed: boolean = false;
    public Why: string = "";
    /** ⭐ TRANSLATION: what went out (sent, and different from what was typed), else "". */
    public SentText: string = "";
    /** ⛔ TRANSLATION: final, NOTHING was sent - the words could not be translated. */
    public TranslateFailed: boolean = false;
}

/** The line under the Send button for a FINAL outcome. `Text` "" means say nothing new. */
export class AppSendLine {
    public Text: string = "";
    /** Shown as trouble (`.trouble`, the alarm colour). ⛔ Never for sent or unconfirmed. */
    public Trouble: boolean = false;
}

export class AppSignUpApi {
    public static readonly Claim: string = "/claim";
    public static readonly Pair: string = "/pair";
    public static readonly Dropbox: string = "/dropbox";
    public static readonly Storage: string = "/storage";
    /** ⚠ The READ ticket, and `/storage` is the write one. See `FolderTicketRules` for why the pair
     *  is two endpoints and two scopes rather than one of each. */
    public static readonly Folder: string = "/folder";
    /** ⚠ Sending, and asking how a send went. Must match `SendRules.Route`. */
    public static readonly Send: string = "/send";
    /**
     * ⚠ Signing back in on a new device with the Dropbox the archive lives in. GET answers the
     * Dropbox page to open; POST exchanges the pasted code for an archive key. Must match
     * `ArchiveSignInRules.Route` (`SignInWire.test.ts`).
     */
    public static readonly SignIn: string = "/signin";

    /**
     * The body of a sign-in. ⛔ NO SECRET AND NO KEY - the Dropbox code IS the proof, and a page
     * signing in on a new device holds neither.
     */
    public static SignInBody = (Phone: string, Code: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Code).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), code: nz(Code).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.SignInBody");
            return "";
        }
    };

    /**
     * The body of "close my account", or "" when it cannot be asked.
     *
     * ⛔ THE KEY IS REQUIRED - `/leave` is authorised by the browser key and nothing else. ⚠ `confirm`
     * IS SENT EVEN WHEN EMPTY: the service answers a missing confirmation with a sentence the
     * subscriber can act on ("type your number"), which is better than this page guessing one.
     * Field names must match `LeaveRules.AskIn` (`LeaveContract.test.ts`).
     */
    // REUSE-JUSTIFIED: a one-line body builder inside the mandated guard shell. `CheckBody`, `SendBody`,
    // `AppSendHistory.Body` and `AppClaudeToken.StatusBody`/`SaveBody` share only the number + key test; each
    // writes its OWN wire contract (pinned per route by `LeaveContract`, `SendHistoryContract`,
    // `AppClaudeToken.test.ts`), and `AppSendHistory`/`AppClaudeToken` import nothing of the sign-up on purpose.
    public static LeaveBody = (Phone: string, Key: string, Confirm: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(),
                confirm: nz(Confirm).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.LeaveBody");
            return "";
        }
    };

    /**
     * Should the archive show "WhatsApp disconnected - re-link"? Only on the service's own `false`.
     * ⛔ See `SignUpAnswer.LinkKnown` for why absence is not `false`.
     */
    public static ShowRelink = (Answer: SignUpAnswer): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Answer.Ok === true && Answer.LinkKnown === true && Answer.Linked === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.ShowRelink");
            return false;
        }
    };

    /** The banner's sentence. ⚠ Ink, not alarm orange - the archive itself is fine. */
    public static RelinkWhy = (): string => {
        // DOCUMENTED EXCEPTION: pure constant sentence - no input, no I/O, cannot throw.
        return "WhatsApp is disconnected, so new messages are not reaching your archive and you"
            + " cannot send from here. Everything already in your archive is safe. Link your phone"
            + " again to carry on.";
    };

    /**
     * ⛔ SHOWN BESIDE EVERY "OPEN DROPBOX" LINK (grant and sign-in). When the Dropbox app is frozen at
     * its user limit, Dropbox says so on ITS OWN page and never issues a code - so nothing ever
     * reaches the service to explain, and without this the claimant is left reading a Dropbox error
     * that tells them to "contact the app developer".
     */
    public static FrozenHint = (): string => {
        // DOCUMENTED EXCEPTION: pure constant sentence - no input, no I/O, cannot throw.
        return "If Dropbox says this app has reached its user limit, that is at our end, not yours:"
            + " your Dropbox account is fine and your WhatsApp link is safe. Please come back and"
            + " try again later.";
    };

    /**
     * The body of a send, or "" when it cannot be sent.
     *
     * ⚠ THE REQUEST TRAVELS AS AN OBJECT, NOT AS A STRING OF JSON, so the service reads it with the
     * same `Outbox.Parse` the sender uses on a file - one parser for both doors.
     */
    // REUSE-JUSTIFIED: see `LeaveBody` - a one-line body builder with its own wire contract (`request` is PARSED
    // here, so a throw is this member's to report); only the number + key test and the shell are shared.
    public static SendBody = (Phone: string, Key: string, RequestJson: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return AppSendHistory.ProvedBody(Phone, Key, { request: JSON.parse(nz(RequestJson)) as unknown });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.SendBody");
            return "";
        }
    };

    /** The body of "how did this message go?". */
    // REUSE-JUSTIFIED: see `LeaveBody` - a one-line body builder with its own wire contract (`check` is NOT
    // trimmed, unlike the proofs); only the number + key test and the shell are shared.
    public static CheckBody = (Phone: string, Key: string, Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0 || nz(Key).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Phone).trim(), key: nz(Key).trim(), check: nz(Id) });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.CheckBody");
            return "";
        }
    };

    /** ⛔ These must match `PairHttpRules.Ask` / `.Poll` - a mismatch is refused, never defaulted. */
    public static readonly Ask: string = "ask";
    public static readonly Poll: string = "poll";

    /**
     * The address of one endpoint on the configured service.
     *
     * ⚠ AN UNCONFIGURED SERVICE URL ANSWERS "", AND THE CALLER REFUSES RATHER THAN GUESSING. A
     * relative path would post to wherever the page happens to be served from, which on the
     * published site is a storage account with no endpoints at all - a sign-up that appears to do
     * nothing, with a 404 nobody sees.
     */
    /** The routes demo mode sends to `/demo/<route>` (the service runs them over the invented archive). */
    public static readonly DemoRoutes: string[] = ["/tasks", "/money", "/export", "/translate", "/chat/settings", "/chat/archive", "/live"];

    public static UrlFor = (ServiceUrl: string, Route: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Base: string = nz(ServiceUrl).trim();
            if (Base.length === 0) { return ""; }
            while (Base.length > 0 && Base.charAt(Base.length - 1) === "/") {
                Base = Base.substring(0, Base.length - 1);
            }
            if (Base.length === 0) { return ""; }
            // ⭐ DEMO MODE: the actions on the invented chats go to the service's `/demo/...` twins (`RouteDemo`).
            if (AppDemo.On() === true && AppSignUpApi.DemoRoutes.indexOf(nz(Route)) >= 0) { return Base + "/demo" + nz(Route); }
            return Base + nz(Route);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.UrlFor");
            return "";
        }
    };

    /** ⚠ THE NUMBER IS SENT AS TYPED. `TenantRoster.KeyOf` normalises it server side, once. */
    public static ClaimBody = (Number: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Number).trim().length === 0) { return ""; }
            return JSON.stringify({ number: nz(Number).trim() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.ClaimBody");
            return "";
        }
    };

    /**
     * ⛔ THE ACTION IS ALWAYS EXPLICIT. `ask` requests a code and `poll` changes nothing; sending
     * `ask` on a timer would issue a second code every cycle, and each new code CANCELS the one
     * before it - while the subscriber is typing it into their phone.
     */
    public static PairBody = (Number: string, Secret: string, Action: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Number).trim().length === 0) { return ""; }
            if (nz(Secret).trim().length === 0) { return ""; }
            if (Action !== AppSignUpApi.Ask && Action !== AppSignUpApi.Poll) { return ""; }
            return JSON.stringify({ number: nz(Number).trim(), secret: nz(Secret).trim(),
                action: Action });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.PairBody");
            return "";
        }
    };

    /**
     * Put the ONE proof this request carries into it: the sign-up secret when the page holds one,
     * otherwise the archive key. False when there is neither.
     *
     * ⛔ THE SECRET WINS, AND ONLY ONE IS EVER SENT. During sign-up the page may also hold a stored
     * key - possibly a PREVIOUS subscriber's, on a shared browser - and the service prefers the
     * secret anyway; sending just the one keeps the sign-up request exactly what it was. The key is
     * for the page that LOST its secret (a reload after linking) and for "Import a backup".
     *
     * DOCUMENTED EXCEPTION: fills an object already in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure helper, called only from guarded builders; cannot throw.
     */
    private static WithProof = (Body: Record<string, unknown>, Secret: string, Key: string): boolean => {
        if (nz(Secret).trim().length > 0) { Body.secret = nz(Secret).trim(); return true; }
        if (nz(Key).trim().length > 0) { Body.key = nz(Key).trim(); return true; }
        return false;
    };

    public static DropboxBody = (Number: string, Secret: string, Code: string,
                                 Key: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Number).trim().length === 0) { return ""; }
            if (nz(Code).trim().length === 0) { return ""; }
            const Out: Record<string, unknown> = { number: nz(Number).trim() };
            if (AppSignUpApi.WithProof(Out, Secret, Key) === false) { return ""; }
            Out.code = nz(Code).trim();
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.DropboxBody");
            return "";
        }
    };

    /**
     * ⛔ THE SIZE IS SENT AND MUST BE THE REAL ONE. It is what the service checks the subscriber's
     * free space against BEFORE a byte moves, so a rounded or guessed figure turns a refusal they
     * could have acted on into an upload that dies at 90%.
     */
    // ⚠ THE PARAMETER IS `Phone`, NOT `Number`, AND ONLY IN THIS METHOD. Every sibling here names it
    // `Number`, which shadows the global of that name - harmless there, and a silent defect here,
    // because this is the one that has to CONVERT a value. `Math.floor(Number(Bytes))` under that
    // shadow is a call on a string.
    public static StorageBody = (Phone: string, Secret: string, Bytes: number,
                                 Key: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0) { return ""; }
            const N: number = Math.floor(Number(Bytes));
            if (isNaN(N) === true || N <= 0) { return ""; }
            const Out: Record<string, unknown> = { number: nz(Phone).trim() };
            if (AppSignUpApi.WithProof(Out, Secret, Key) === false) { return ""; }
            Out.bytes = N;
            return JSON.stringify(Out);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.StorageBody");
            return "";
        }
    };

    /**
     * The body of a request to open the archive.
     *
     * ⚠ NO SIZE, AND THAT IS THE DIFFERENCE FROM `StorageBody` - nothing is being uploaded, so
     * there is no room to make. ⛔ An empty number or secret answers "" and the caller refuses
     * rather than sending: `FolderTicketRules.AskIn` requires both, and a blank secret meeting a
     * blank held one is the comparison `SignUpSession.Matches` exists to refuse.
     */
    public static FolderBody = (Phone: string, Secret: string, Key: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim().length === 0) { return ""; }
            const One: string = nz(Secret).trim();
            const Two: string = nz(Key).trim();
            // ⛔ AT LEAST ONE PROOF, OR NOTHING IS SENT. `FolderTicketRules.AskIn` requires the same,
            // and a body with neither would spend a round trip to be told what this already knows.
            if (One.length === 0 && Two.length === 0) { return ""; }
            // ⚠ BOTH ARE SENT WHEN BOTH ARE HELD, which is every caller in the two hours after
            // sign-up. The service prefers the secret because that is the proof that MINTS a key,
            // so sending only the key there would leave the subscriber without one.
            // `tz`: the browser's own zone, kept per subscriber so the pages count on their clock
            // (`OwnerSettings`); the service ignores anything ICU does not know.
            return JSON.stringify({ number: nz(Phone).trim(), secret: One, key: Two, tz: AppTimeZone.Here() });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.FolderBody");
            return "";
        }
    };

    /**
     * Read whatever came back.
     *
     * ⛔ THE BODY IS READ WHATEVER THE STATUS. The service states its refusals IN the body, and a
     * transport that threw on a non-2xx would replace the one sentence that says what to do with
     * "request failed" - in the flow a stranger meets first.
     */
    public static Answer = (Status: number, Text: string): SignUpAnswer => {
        if (getCoreUtils().KErrorMode === true) { return AppSignUpApi.No(AppSignUpApi.OursWhy()); }
        try {
            if (Status === 0) { return AppSignUpApi.No(AppSignUpApi.OursWhy()); }
            const Raw: Record<string, unknown> = JSON.parse(nz(Text)) as Record<string, unknown>;
            if (Raw === null || typeof Raw !== "object") {
                return AppSignUpApi.No(AppSignUpApi.OursWhy());
            }
            return AppSignUpApi.Fields(Raw);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: an answer this build cannot parse is an EXPECTED
            // condition on a public wire (law 1, fate 1) - a proxy error page, a truncated reply.
            // Reporting it would flip the gate and blank the page the visitor is trying to use.
            return AppSignUpApi.No(AppSignUpApi.OursWhy());
        }
    };

    /**
     * DOCUMENTED EXCEPTION: reads fields already parsed.
     * ERROR-SUPPRESSED-JUSTIFIED: pure reader, called only from a guarded method.
     *
     * ⛔ BOOLEANS ARE READ AS STRICTLY `true`. The string "false" is truthy, and reading `linked`
     * loosely would tell a subscriber their phone was connected when it is not - ending the step
     * on a screen they cannot get back from.
     */
    private static Fields = (Raw: Record<string, unknown>): SignUpAnswer => {
        const Out: SignUpAnswer = new SignUpAnswer();
        Out.Ok = Raw.ok === true;
        Out.Why = nz(Raw.why as string);
        Out.Number = nz(Raw.number as string);
        Out.Secret = nz(Raw.secret as string);
        Out.Code = nz(Raw.code as string);
        Out.Linked = Raw.linked === true;
        Out.LinkKnown = typeof Raw.linked === "boolean";
        Out.Waiting = Raw.waiting === true;
        Out.Passkey = Raw.passkey === true;
        Out.Full = Raw.full === true;
        Out.History = Raw.history === true;
        Out.Url = nz(Raw.url as string);
        Out.Account = nz(Raw.account as string);
        Out.Token = nz(Raw.token as string);
        Out.Expires = AppSignUpApi.Seconds(Raw.expires);
        Out.Path = nz(Raw.path as string);
        Out.Key = nz(Raw.key as string);
        Out.Id = nz(Raw.id as string);
        Out.Queued = Raw.queued === true;
        Out.Sent = Raw.sent === true;
        Out.Unconfirmed = Raw.unconfirmed === true;
        Out.SentText = nz(Raw.sentText as string);
        Out.TranslateFailed = Raw.translateFailed === true;
        Out.NoStorage = Raw.nostorage === true;
        Out.Stale = Raw.stale === true;
        Out.Owner = Raw.owner === true;
        if (Raw.storage === "onedrive" || Raw.storage === "dropbox") { Out.Storage = Raw.storage as string; }
        Out.UploadUrl = nz(Raw.uploadUrl as string);
        return Out;
    };

    /**
     * What a `/send` check answer means for the page's poll.
     *
     * ⚠ A LOST CONNECTION IS "KEEP WAITING", NOT A VERDICT. `Answer` reports status 0 as `OursWhy`,
     * and treating that as final would tell somebody their message failed during a network blip
     * while the sender was, at that moment, sending it. Only the service's OWN answer is final.
     * ⛔ UNCONFIRMED IS FINAL AND IS CHECKED BEFORE "KEEP WAITING": it arrives as `ok: true`, and
     * reading it as waiting would poll for a second receipt the sender will never write.
     */
    public static SendVerdictOf = (Answer: SignUpAnswer): AppSendVerdict => {
        if (getCoreUtils().KErrorMode === true) { return new AppSendVerdict(); }
        try {
            const Out: AppSendVerdict = new AppSendVerdict();
            if (Answer.Ok === true && Answer.Sent === true) {
                Out.Sent = true;
                Out.Final = true;
                Out.SentText = nz(Answer.SentText);
                return Out;
            }
            // ⛔ TRANSLATION: a refusal that means NOTHING was sent because the words could not be translated.
            if (Answer.Ok !== true && Answer.TranslateFailed === true) {
                Out.Final = true;
                Out.TranslateFailed = true;
                Out.Why = nz(Answer.Why);
                return Out;
            }
            if (Answer.Ok === true && Answer.Unconfirmed === true) {
                Out.Final = true;
                Out.Unconfirmed = true;
                Out.Why = AppSignUpApi.UnconfirmedLine(Answer.Why);
                return Out;
            }
            if (Answer.Ok === true) { return Out; }
            if (nz(Answer.Why) === AppSignUpApi.OursWhy()) { return Out; }
            Out.Final = true;
            Out.Why = nz(Answer.Why);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.SendVerdictOf");
            return new AppSendVerdict();
        }
    };

    /**
     * What the page says once a watched send is FINAL. `AppMain.OnSendState` paints it.
     *
     * ⛔ UNCONFIRMED IS ITS OWN LINE, IN INK, NOT ALARM ORANGE. It is not "Sent." (nobody confirmed
     * it), not a failure (it may have arrived - orange reads as "try again", and trying again is
     * how it goes twice) and not "still queued" (the sender will never try it again).
     */
    public static SendLineFor = (Sent: boolean, Unconfirmed: boolean, Why: string): AppSendLine => {
        if (getCoreUtils().KErrorMode === true) { return new AppSendLine(); }
        try {
            const Out: AppSendLine = new AppSendLine();
            if (Sent === true) { Out.Text = "Sent."; return Out; }
            if (Unconfirmed === true) { Out.Text = AppSignUpApi.UnconfirmedLine(Why); return Out; }
            Out.Text = nz(Why);
            Out.Trouble = Out.Text.length > 0;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.SendLineFor");
            return new AppSendLine();
        }
    };

    /**
     * The line shown for an unconfirmed send: the sender's own sentence, or this one if it sent
     * none. ⛔ Either way it must say to check the phone BEFORE sending again - never "sent", never
     * "not sent", and never "still queued", because all three invite a second copy.
     */
    public static UnconfirmedLine = (Why: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Why).trim().length > 0) { return nz(Why).trim(); }
            return "WhatsApp did not confirm this message in time, so it may or may not have been"
                + " delivered. It will not be sent again automatically - check the chat on your"
                + " phone before sending it again.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUpApi.UnconfirmedLine");
            return "";
        }
    };

    /**
     * A lifetime in seconds, or 0.
     *
     * ⚠ AN UNREADABLE ONE IS 0 - "already expired" - NEVER FOREVER. `DropboxAuth.SecondsIn` made the
     * same call for the same reason: reading `NaN` as a long life caches a token that does not work
     * and retries nothing, while reading it as zero costs one avoidable refusal.
     *
     * DOCUMENTED EXCEPTION: converts one value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: pure reader, called only from a guarded method.
     */
    private static Seconds = (Raw: unknown): number => {
        const N: number = Math.floor(Number(Raw));
        if (isNaN(N) === true || N < 0) { return 0; }
        return N;
    };

    /**
     * ⚠ STATUS 0 IS A LOST CONNECTION OR A BLOCKED CROSS-ORIGIN ANSWER AND THE PAGE CANNOT TELL, so
     * it names neither. `SignUpRules` ruled this: telling somebody whose connection is fine to try
     * their number again sends them round a loop that cannot work while the real cause - an origin
     * missing from `WAC_SIGNUP_ORIGINS` - is never said aloud. The operator's evidence is the
     * setting; the visitor's sentence is that it is our end.
     */
    public static OursWhy = (): string => {
        // DOCUMENTED EXCEPTION: pure constant sentence - no input, no I/O, cannot throw; it is also
        // what the transport answers on status 0, so gating it would blank that answer.
        return "WACommander could not be reached just now. This is at our end - nothing is wrong"
            + " with what you typed. Please try again in a moment.";
    };

    /** ⚠ NO SERVICE CONFIGURED IS THE DEPLOYMENT'S FAULT and must not read as the visitor's. */
    public static NoServiceWhy = (): string => {
        // DOCUMENTED EXCEPTION: pure constant sentence - no input, no I/O, cannot throw; every
        // caller is a guarded method.
        return "WACommander is not finished setting up here, so sign-up cannot run yet. This is at"
            + " our end.";
    };

    /** DOCUMENTED EXCEPTION, builds a refusal from a sentence in hand.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static No = (Why: string): SignUpAnswer => {
        const Out: SignUpAnswer = new SignUpAnswer();
        Out.Ok = false;
        Out.Why = Why;
        return Out;
    };
}
